import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPressReleaseData, formatDate, convertDriveUrl } from '../../services/googleSheets';
import '../NoticeList.css';
import ShareButton from '../ShareButton';
import URLPreview from '../URLPreview';

interface PressReleaseItem {
  id: number;
  카테고리: string;
  제목: string;
  내용: string;
  작성일: string;
  첨부파일URL?: string;
  첨부파일이름?: string;
}

const PressRelease: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pressReleases, setPressReleases] = useState<PressReleaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRelease, setSelectedRelease] = useState<PressReleaseItem | null>(null);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchPressReleases = async () => {
      try {
        const data = await getPressReleaseData();
        // 최신순으로 정렬 (보도자료 시트에서 가져오므로 별도 필터링 불필요)
        const sortedData = data.sort((a: any, b: any) => b.id - a.id);
        setPressReleases(sortedData as PressReleaseItem[]);
        
        // URL에 id가 있으면 해당 보도자료를 선택
        if (id) {
          const release = sortedData.find((item: any) => item.id === parseInt(id));
          if (release) {
            setSelectedRelease(release as PressReleaseItem);
          }
        }
      } catch (error) {
        console.error('보도자료를 불러오는데 실패했습니다:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPressReleases();
  }, [id]);

  // 페이지네이션 계산
  const totalPages = Math.ceil(pressReleases.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentReleases = pressReleases.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReleaseClick = (release: PressReleaseItem) => {
    navigate(`/press-release/${release.id}`);
  };

  const handleBackToList = () => {
    setSelectedRelease(null);
    navigate('/press-release');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // YouTube URL을 embed URL로 변환하는 함수
  const getYouTubeEmbedUrl = (url: string) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`;
    }
    return null;
  };

  // 내용에서 YouTube 링크와 일반 URL을 찾아서 처리하는 함수
  const renderContentWithMediaAndLinks = (content: string) => {
    if (!content) return '';

    // YouTube URL 정규식
    const youtubeRegex = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[\w-]+)/g;
    // 일반 URL 정규식 (YouTube 제외)
    const urlRegex = /(https?:\/\/(?!(?:www\.)?(?:youtube\.com|youtu\.be))[^\s<>"{}|\\^`[\]]+)/g;
    
    const elements: React.ReactNode[] = [];
    let lastIndex = 0;
    let elementIndex = 0;

    // YouTube URL 찾기
    let youtubeMatch;
    const youtubeMatches: Array<{match: string, index: number}> = [];
    while ((youtubeMatch = youtubeRegex.exec(content)) !== null) {
      youtubeMatches.push({match: youtubeMatch[0], index: youtubeMatch.index});
    }

    // 일반 URL 찾기
    let urlMatch;
    const urlMatches: Array<{match: string, index: number}> = [];
    while ((urlMatch = urlRegex.exec(content)) !== null) {
      urlMatches.push({match: urlMatch[0], index: urlMatch.index});
    }

    // 모든 매치를 인덱스 순으로 정렬
    const allMatches = [...youtubeMatches.map(m => ({...m, type: 'youtube'})), ...urlMatches.map(m => ({...m, type: 'url'}))]
      .sort((a, b) => a.index - b.index);

    // 텍스트와 미디어를 순서대로 렌더링
    allMatches.forEach((match, i) => {
      // 이전 매치와 현재 매치 사이의 텍스트 추가
      if (match.index > lastIndex) {
        const textBefore = content.substring(lastIndex, match.index);
        if (textBefore.trim()) {
          elements.push(
            <span key={`text-${elementIndex++}`} style={{ whiteSpace: 'pre-line' }}>
              {textBefore}
            </span>
          );
        }
      }

      // 매치된 URL 처리
      if (match.type === 'youtube') {
        const embedUrl = getYouTubeEmbedUrl(match.match);
        if (embedUrl) {
          elements.push(
            <div key={`youtube-${elementIndex++}`} style={{ margin: '2rem 0' }}>
              <iframe
                width="100%"
                height="400"
                src={embedUrl}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
            </div>
          );
        }
      } else if (match.type === 'url') {
        elements.push(
          <URLPreview key={`url-${elementIndex++}`} url={match.match} />
        );
      }

      lastIndex = match.index + match.match.length;
    });

    // 마지막 매치 이후의 텍스트 추가
    if (lastIndex < content.length) {
      const textAfter = content.substring(lastIndex);
      if (textAfter.trim()) {
        elements.push(
          <span key={`text-${elementIndex++}`} style={{ whiteSpace: 'pre-line' }}>
            {textAfter}
          </span>
        );
      }
    }

    // 매치가 없으면 원본 텍스트 반환
    if (elements.length === 0) {
      return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>;
    }

    return elements;
  };

  // 첨부파일 표시 컴포넌트 (상세보기용 - 전체 파일명)
  const AttachmentDisplay: React.FC<{ url: string; fileName?: string }> = ({ url, fileName }) => {
    const downloadUrl = convertDriveUrl(url) || url;
    const displayName = fileName || '첨부파일';
    
    return (
      <a 
        href={downloadUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="notice-attachment-inline"
        title={displayName}
        style={{
          maxWidth: 'none',
          whiteSpace: 'nowrap'
        }}
      >
        <span className="attachment-icon">📎</span>
        <span className="attachment-name" style={{ 
          maxWidth: 'none',
          overflow: 'visible',
          textOverflow: 'unset',
          whiteSpace: 'nowrap'
        }}>
          {displayName}
        </span>
      </a>
    );
  };

  // 첨부파일 표시 컴포넌트 (목록용 - 축약된 파일명)
  const AttachmentDisplayList: React.FC<{ url: string; fileName?: string }> = ({ url, fileName }) => {
    const downloadUrl = convertDriveUrl(url) || url;
    const displayName = fileName || '첨부파일';
    
    return (
      <a 
        href={downloadUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="notice-attachment-inline"
        title={displayName}
      >
        <span className="attachment-icon">📎</span>
        <span className="attachment-name" style={{ 
          maxWidth: '120px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {displayName}
        </span>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="notice-loading">
        <div className="loading-spinner"></div>
        <p>보도자료를 불러오는 중...</p>
      </div>
    );
  }

  // 상세보기 화면
  if (selectedRelease) {
    return (
      <div className="notice-list-container">
        <div className="notice-header">
          <h1>📰 보도자료</h1>
          <p>청소년 스마트폰 프리 운동본부의 공식 보도자료를 확인하세요</p>
        </div>
        <div className="notice-list" style={{ padding: '2rem' }}>
          <div className="notice-header" style={{ marginBottom: '2rem', textAlign: 'left' }}>
            <h1 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>{selectedRelease.제목}</h1>
            <div style={{ 
              display: 'flex', 
              gap: '1rem', 
              fontSize: '0.9rem', 
              color: '#666',
              paddingBottom: '1rem',
              borderBottom: '2px solid #e74c3c'
            }}>
              <span>📅 {formatDate(selectedRelease.작성일)}</span>
              {selectedRelease.카테고리 && (
                <span>🏷️ {selectedRelease.카테고리}</span>
              )}
            </div>
          </div>
          
          <div style={{ 
            lineHeight: '1.8', 
            color: '#2c3e50', 
            marginBottom: '2rem'
          }}>
            {renderContentWithMediaAndLinks(selectedRelease.내용)}
          </div>
          
          {selectedRelease.첨부파일URL && (
            <div style={{
              borderTop: '1px solid #eee',
              paddingTop: '1.5rem',
              marginBottom: '2rem'
            }}>
              <h3 style={{ color: '#2c3e50', marginBottom: '1rem', fontSize: '1.1rem' }}>
                첨부파일
              </h3>
              <AttachmentDisplay 
                url={selectedRelease.첨부파일URL} 
                fileName={selectedRelease.첨부파일이름}
              />
            </div>
          )}
          
          <div style={{
            borderTop: '1px solid #eee',
            paddingTop: '1.5rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <button
              onClick={handleBackToList}
              style={{
                background: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                fontWeight: '600',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#5a6268'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#6c757d'}
            >
              목록
            </button>
            <ShareButton title={`${selectedRelease.제목} - 청소년 스마트폰 프리 운동`} />
          </div>
        </div>
      </div>
    );
  }

  // 목록 화면
  return (
    <div className="notice-list-container">
      <div className="notice-header">
        <h1>📰 보도자료</h1>
        <p>청소년 스마트폰 프리 운동본부의 공식 보도자료를 확인하세요</p>
      </div>

      <div className="notice-stats">
        <span>전체 {pressReleases.length}건</span>
        <span>페이지 {currentPage} / {totalPages}</span>
      </div>

      <div className="notice-list">
        {currentReleases.length > 0 ? (
          <>
            {/* 데스크톱 테이블 뷰 */}
            <table className="notice-table">
              <thead>
                <tr>
                  <th className="notice-number">번호</th>
                  <th className="notice-category-header" style={{
                    width: '100px',
                    textAlign: 'center'
                  }}>카테고리</th>
                  <th className="notice-title-header">제목</th>
                  <th className="notice-attachment-header" style={{
                    textAlign: 'left'
                  }}>첨부</th>
                  <th className="notice-date-header">작성일</th>
                </tr>
              </thead>
              <tbody>
                {currentReleases.map((release) => (
                  <tr key={release.id} className="notice-row">
                    <td className="notice-number">{release.id}</td>
                    <td className="notice-category-cell" style={{
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      whiteSpace: 'nowrap',
                      width: '100px'
                    }}>
                      {release.카테고리 || '-'}
                    </td>
                    <td className="notice-title-cell">
                      <button 
                        onClick={() => handleReleaseClick(release)}
                        className="notice-link"
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                          padding: 0
                        }}
                      >
                        {release.제목 || '제목 없음'}
                      </button>
                    </td>
                    <td className="notice-attachment-cell" style={{
                      textAlign: 'left'
                    }}>
                      {release.첨부파일URL && (
                        <AttachmentDisplayList 
                          url={release.첨부파일URL} 
                          fileName={release.첨부파일이름}
                        />
                      )}
                    </td>
                    <td className="notice-date-cell">{formatDate(release.작성일)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 모바일 카드 뷰 */}
            <div className="notice-cards">
              {currentReleases.map((release) => (
                <div key={release.id} className="notice-card">
                  <div className="notice-card-header">
                    <div className="notice-card-info">
                      <span className="notice-card-number">
                        📄 {release.id}
                      </span>
                      <span className="notice-card-date">
                        📅 {formatDate(release.작성일)}
                      </span>
                      {release.카테고리 && (
                        <span className="notice-card-category">
                          🏷️ {release.카테고리}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div 
                    className="notice-card-title"
                    onClick={() => handleReleaseClick(release)}
                  >
                    {release.제목 || '제목 없음'}
                  </div>
                  
                  {release.내용 && (
                    <div className="notice-card-content">
                      {release.내용.substring(0, 100)}...
                    </div>
                  )}
                  
                  <div className="notice-card-footer">
                    <div></div>
                    {release.첨부파일URL && (
                      <AttachmentDisplayList 
                        url={release.첨부파일URL} 
                        fileName={release.첨부파일이름}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="notice-empty">
            <p>등록된 보도자료가 없습니다.</p>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ‹ 이전
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            다음 ›
          </button>
        </div>
      )}
    </div>
  );
};

export default PressRelease; 